import { useEffect, useState } from 'react';
import { v4 as uuid4 } from 'uuid';
import Voca from './Voca';
import Loading from '../../common/Loading';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {updateWord, validateWord, initializeValidationResult} from '../../../modules/admin/voca';
import validateInput from '../../../utils/validateInput';
import FeedbackMessage from '../../admin/feedback/FeedbackMessage';
import {ReactComponent as EmptyIcon} from '../../../assets/feedback/empty.svg';
import {ReactComponent as NoSearchIcon} from '../../../assets/feedback/nosearch.svg';
import {ReactComponent as ErrorIcon} from '../../../assets/feedback/error.svg';
import {ReactComponent as Redo} from '../../../assets/Icon_redo.svg';
import {LuPencilLine} from 'react-icons/lu';
import styles from './styles/voca.module.css';

export default function VocaTable({ listResponse, listErrorResponse, listLoading, deleteVoca, handleChangeDeleteCheckbox, hasFilterParams }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [updateMode, setUpdateMode] = useState({});
    const [wordStates, setWordStates] = useState({});
    const [newUseWord, setNewUseWord] = useState({});
    const [errors, setErrors] = useState({});
    const [errorsStd, setErrorsStd] = useState({});
    const [isStdValidated, setIsStdValidated] = useState({});
    const ERROR_MESSAGE_ALERT_DANGER = '입력 값에 허용되지 않는 특수문자 또는 위험한 코드가 포함되어 있습니다.';
    const ERROR_MESSAGE_DANGER_INPUT = '유효한 단어를 입력해 주세요';
    const ERROR_MESSAGE_SERVER_ERROR = '서버 오류로 등록에 실패했어요.';
    const ERROR_MESSAGE_DUPLICATED = '중복되는 단어가 있어요.';

    const handleUpdateMode = (id) => {
        if (!updateMode[id]) {
            // off -> on
            initializeUpdateMode();
        }
        setUpdateMode(prevState => ({
            ...prevState,
            [id]: !prevState[id]
        }));
        if (!updateMode[id]) {
            // on -> off
            initializeWordStates();
            initializeErrors(id);
        }
    };

    const initializeUpdateMode = () => {
        setUpdateMode(prevState => {
            const switchOff = Object.keys(prevState).reduce((id, key) => {
                id[key] = false;
                return id;
            }, {});
            return switchOff;
        });
    };

    const initializeErrors = (id) => {
        setIsStdValidated(prevState => ({
            ...prevState,
            [id]: true
        }));
        setErrors(prevState => ({
            ...prevState,
            [id]: ''
        }));
        setErrorsStd(prevState => ({
            ...prevState,
            [id]: ''
        }));
    };

    const handleChangeNewStandardWord = (id, name, value) => {
        setWordStates(prevState => ({
            ...prevState,
            [id]: {
                ...prevState[id],
                [name]: value
            }
        }));
        setIsStdValidated(prevState => ({
            ...prevState,
            [id]: false
        }));
    };

    const handleChangeNewUseWord = (id, value) => {
        setNewUseWord(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleOnBlurChangeNewStandardWord = async (e, id) => {
        try {
            // { isValid, invalidTarget }
            const result = await new Promise((resolve, reject) => {
                dispatch(validateWord({ category: 'WORD', target: 'STANDARD', value: wordStates[id].standardWord.trim(), resolve, reject }))
            });
            if (!result) {
                setErrorsStd(prevState => ({
                    ...prevState,
                    [id]: ERROR_MESSAGE_SERVER_ERROR
                }));
                return;
            }
            
            if (result.isValid === true) {
                setErrorsStd(prevState => ({
                    ...prevState,
                    [id]: ''
                }));
                setIsStdValidated(prevState => ({
                    ...prevState,
                    [id]: true
                }));
            } else {
                setErrorsStd(prevState => ({
                    ...prevState,
                    [id]: ERROR_MESSAGE_DUPLICATED
                }));
            }    
        } catch (e) {
            console.error(e);
            setErrorsStd(prevState => ({
                ...prevState,
                [id]: ERROR_MESSAGE_SERVER_ERROR
            }));
        }
    };

    const validateDuplicatedLocalUseWord = (id, inputWord) => {
        const filtered = wordStates[id].useWordList;
        if (checkKoreanCulture(inputWord)) {
            if (filtered.includes(inputWord)) return true;
        } else {
            if (filtered.some(word => word.toUpperCase() === inputWord.toUpperCase())) return true;
        }
        return false;
    };

    const checkKoreanCulture = (input) => {
        return /[가-힣]/.test(input);
    };

    const handleAddWord = async (e, id) => {
        if (e.key === 'Enter' && newUseWord[id].trim() !== '') {
            const inputWord = newUseWord[id].trim();
            if (!validateInput(inputWord)) {
                alert(ERROR_MESSAGE_ALERT_DANGER);
                setWordStates(prevState => ({
                    ...prevState,
                    [id]: {
                        ...prevState[id],
                        useWordList: [...prevState[id].useWordList, inputWord]
                    }
                }));
                setErrors(prevState => ({
                    ...prevState,
                    [id]: ERROR_MESSAGE_DANGER_INPUT
                }));
                setNewUseWord(prevState => ({
                    ...prevState,
                    [id]: ''
                }));
                return;
            }
    
            if (validateDuplicatedLocalUseWord(id, inputWord)) {
                setWordStates(prevState => ({
                    ...prevState,
                    [id]: {
                        ...prevState[id],
                        useWordList: [...prevState[id].useWordList, inputWord]
                    }
                }));
                setErrors(prevState => ({
                    ...prevState,
                    [id]: ERROR_MESSAGE_DUPLICATED
                }));
                setNewUseWord(prevState => ({
                    ...prevState,
                    [id]: ''
                }));
                return;
            }
    
            try {
                const result = await new Promise((resolve, reject) => {
                    dispatch(validateWord({ category: 'WORD', target: 'USE', value: inputWord, resolve, reject }))
                });
                if (!result) {
                    setErrors(prevState => ({
                        ...prevState,
                        [id]: ERROR_MESSAGE_SERVER_ERROR
                    }));
                    return;
                }

                setWordStates(prevState => ({
                    ...prevState,
                    [id]: {
                        ...prevState[id],
                        useWordList: [...prevState[id].useWordList, inputWord]
                    }
                }));
                if (result.isValid === true) {
                    setErrors(prevState => ({
                        ...prevState,
                        [id]: ''
                    }));
                } else {
                    setErrors(prevState => ({
                        ...prevState,
                        [id]: ERROR_MESSAGE_DUPLICATED
                    }));    
                }    
            } catch (e) {
                console.error(e);
                setErrors(prevState => ({
                    ...prevState,
                    [id]: ERROR_MESSAGE_SERVER_ERROR
                }));
            }

            setNewUseWord(prevState => ({
                ...prevState,
                [id]: ''
            }));
        }
    };

    const handleDeleteWord = (id, index) => {
        setWordStates(prevState => {
            const updatedUseWordList = prevState[id].useWordList.filter((_, i) => i !== index);
            return {
                ...prevState,
                [id]: {
                    ...prevState[id],
                    useWordList: updatedUseWordList
                }
            };
        });

        setErrors(prevState => {
            const updatedUseWordList = wordStates[id].useWordList.filter((_, i) => i !== index);
            if (new Set(updatedUseWordList).size === updatedUseWordList.length) {
                return {
                    ...prevState,
                    [id]: ''
                };
            }
            return prevState;
        });
    };

    const handleUpdate = (id) => {
        const toUpdatedWord = wordStates[id];
        const { standardWord, useWordList } = toUpdatedWord;
        const allCaps = useWordList.map(x => checkKoreanCulture(x) ? x : x.toUpperCase());
        dispatch(updateWord({ category: 'WORD', id, standardWord, useWordList: allCaps }));
        setUpdateMode({});
    };

    const onChangeCheckboxOnHeader = (e) => {
        if (!e.target.checked) {
            handleChangeDeleteCheckbox('clear');
            return;
        }
        if (!listResponse) return;
        handleChangeDeleteCheckbox('all');
    }

    const initializeWordStates = () => {
        const initialWordStates = {};
        if (!listLoading && listResponse) {
            listResponse.forEach((word) => {
                initialWordStates[word.id] = {
                    standardWord: word.standardWord,
                    useWordList: word.useWordList
                };
            });
            setWordStates(initialWordStates);
        }
    };

    useEffect(() => {
        initializeUpdateMode();
        initializeWordStates();
    }, [listResponse]);

    const isAnyUpdateMode = Object.values(updateMode).some(value => value);

    useEffect(() => {
        return () => {
            dispatch(initializeValidationResult());
        }
    }, []);

    return (
        <table className={`${styles.table} ${isAnyUpdateMode ? styles.updateMode : ''}`}>
            <thead>
                <tr style={{width: '100%'}}>
                    <th style={{width : '2%'}}>
                        <input type='checkbox' id='select-all' checked={ deleteVoca && deleteVoca.length > 0 && listResponse && deleteVoca.length === listResponse.length } 
                               onChange={onChangeCheckboxOnHeader}/>
                        <label htmlFor='select-all'></label>
                    </th>
                    <th style={{width: '28.4%'}}>
                        <span>표준 단어</span>
                    </th>
                    <th style={{width: '59.6%'}} className={styles.useWordHeader}>사용 단어</th>
                    <th style={{width: '10%'}}>{}</th>
                </tr>
            </thead>
            <tbody>
                {!listLoading && !listErrorResponse && listResponse && listResponse.length > 0 &&
                    listResponse.map((item) => (
                        <tr key={item.id} style={updateMode[item.id] ? {backgroundColor: 'var(--background_purple)'} : {}}>
                            <td style={{width: '2%'}}>
                                <input type='checkbox'
                                    id={`checkbox-${item.id}`}
                                    checked={deleteVoca.includes(item.id)}
                                    onChange={() => handleChangeDeleteCheckbox(item.id)}
                                />
                                <label htmlFor={`checkbox-${item.id}`}></label>
                            </td>
                            <td style={{width: '28.4%'}} onClick={() => { if (!updateMode[item.id]) handleUpdateMode(item.id); }}>
                                {updateMode[item.id]
                                    ? <div style={{display: 'flex', flexDirection: 'row', gap: '6px'}}>
                                        <input className={styles.editInputWord} style={errorsStd[item.id] ? {color: 'red'} : {}}
                                             value={wordStates[item.id].standardWord}
                                             onChange={(e) => handleChangeNewStandardWord(item.id, 'standardWord', e.target.value)}
                                             onBlur={(e) => handleOnBlurChangeNewStandardWord(e, item.id)}
                                             autoComplete='off'
                                        />
                                        {errorsStd[item.id] && <p className={styles.errorMessage}>{errorsStd[item.id]}</p>}                                    
                                    </div>
                                    : item.standardWord
                                }
                            </td>
                            <td style={{width: '59.6%'}} className={`${updateMode[item.id] ? styles.editMode : ''}`} onClick={() => { if (!updateMode[item.id]) handleUpdateMode(item.id); }}>
                                <div className={styles.useWordTd}>
                                {updateMode[item.id]
                                    ? <>
                                        {wordStates[item.id].useWordList.map((useWord, index) => (
                                            <Voca key={uuid4()}
                                                id={item.id}
                                                index={wordStates[item.id].useWordList.indexOf(useWord)}
                                                useWord={useWord}
                                                updateMode={updateMode}
                                                handleDeleteWord={handleDeleteWord}
                                                isError={errors[item.id] && index === (wordStates[item.id].useWordList.length - 1)}
                                            />
                                        ))}
                                    </>
                                    : <>
                                        {item.useWordList.map((useWord) => (
                                            <Voca key={uuid4()}
                                                id={item.id}
                                                useWord={useWord}
                                                updateMode={updateMode}
                                                isError={null}
                                            />
                                        ))}
                                    </>
                                }
                                {updateMode[item.id] && errors[item.id] && <p className={styles.errorMessage}>{errors[item.id]}</p>}
                                {updateMode[item.id] && !errors[item.id]
                                    ? <input className={styles.editInputWord}
                                             placeholder='단어를 입력하세요'
                                             value={newUseWord[item.id]}
                                             onChange={(e) => handleChangeNewUseWord(item.id, e.target.value)}
                                             onKeyUp={(e) => handleAddWord(e, item.id)}
                                             autoComplete='off'
                                    />
                                    : null
                                }
                                </div>
                            </td>
                            <td style={{width: '10%'}}>
                                {updateMode[item.id]
                                    ? <div className={styles.buttonContainer}>
                                        <button className={styles.updateButton} style={errors[item.id] || errorsStd[item.id] || !isStdValidated[item.id] ? {backgroundColor: 'var(--background_purple)'} : {}} disabled={errors[item.id] || errorsStd[item.id] || !isStdValidated[item.id]} onClick={() => handleUpdate(item.id)}>수정하기</button>
                                        <button className={styles.cancelButton} onClick={() => handleUpdateMode(item.id)}>취소</button>
                                    </div>
                                    : !isAnyUpdateMode && <LuPencilLine onClick={() => handleUpdateMode(item.id)} className={styles.pencilIcon} />
                                }
                            </td>
                        </tr>
                    ))
                }
                {!listLoading && !listErrorResponse && listResponse && listResponse.length === 0 && hasFilterParams &&
                    <tr>
                        <td colSpan={4}>
                            <FeedbackMessage icon={<NoSearchIcon/>}
                                            message='검색된 결과 없음'
                                            subMessage='필터를 삭제하고 다시 시도해 보세요.'
                            />
                        </td>
                    </tr>
                }
                {!listLoading && !listErrorResponse && listResponse && listResponse.length === 0 && !hasFilterParams &&
                    <tr>
                        <td colSpan={4}>
                            <FeedbackMessage icon={<EmptyIcon/>}
                                            message='등록된 단어 없음'
                                            subMessage='새로운 단어를 등록해 주세요.'
                            />
                        </td>
                    </tr>
                }
                {!listLoading && listErrorResponse &&
                    <tr>
                        <td colSpan={4}>
                            <FeedbackMessage icon={<ErrorIcon/>}
                                            message={<p style={{ margin: 0, marginBottom: '8px', fontSize: '16px'}}>페이지를 불러오는 중 <span style={{color: 'red' }}>오류</span> 발생</p>}
                                            subMessage={
                                                <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', rowGap: '6px', cursor: 'pointer'}} onClick={() => navigate(0)}>
                                                    <span>재시도</span>                                            
                                                    <Redo style={{marginLeft:'3px', transform: 'scaleX(-1)'}}/>
                                                </div>
                                            }
                            />
                        </td>
                    </tr>
                }
                {listLoading &&
                    <tr style={{backgroundColor: 'var(--background_purple)'}}>
                        <td colSpan={4}>
                            <FeedbackMessage icon={<Loading/>}
                                            message=''
                                            subMessage=''
                            />
                        </td>
                    </tr>
                }
            </tbody>
        </table>
    );
}
